import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { collectBetterDocErrorMessages } from "@/modules/admin/better-doc/lib/collect-better-doc-error-messages"
import { isBetterDocTextPresent } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import {
  BetterDocRemoteMedic,
  fetchBetterDocRemoteRecommendation,
} from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationQuery"
import { useSimulateBetterDocPatientSelectionMutation } from "@/modules/admin/better-doc/services/useSimulateBetterDocPatientSelectionMutation"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"

type BetterDocPatientSelectionDialogProps = {
  sessionId: number
  recommendationId: string
  onClose: () => void
  onCompleted: (outcome: BetterDocSessionActionOutcome) => void
}

type SelectableMedic = {
  medhubId: number
  label: string
}

type RecommendationRequest = {
  recommendationId: string
}

const fetchRecommendationForRequest = ({ recommendationId }: RecommendationRequest) =>
  fetchBetterDocRemoteRecommendation(recommendationId)

const collectSelectableMedics = (medics: ReadonlyArray<BetterDocRemoteMedic>): ReadonlyArray<SelectableMedic> => {
  const selectableMedicsByMedhubId = new Map<number, SelectableMedic>()

  for (const medic of medics) {
    if (!selectableMedicsByMedhubId.has(medic.medhubId)) {
      const label = [medic.name, medic.workplace?.name, medic.workplace?.city]
        .filter(isBetterDocTextPresent)
        .join(" · ")
      selectableMedicsByMedhubId.set(medic.medhubId, { medhubId: medic.medhubId, label })
    }
  }

  return [...selectableMedicsByMedhubId.values()]
}

export const BetterDocPatientSelectionDialog = ({
  sessionId,
  recommendationId,
  onClose,
  onCompleted,
}: BetterDocPatientSelectionDialogProps) => {
  const { t } = useTranslation()
  const [recommendationRequest] = useState<RecommendationRequest>({ recommendationId })
  const recommendationState = useBetterDocFetch(recommendationRequest, fetchRecommendationForRequest)
  const [selectedMedicMedhubId, setSelectedMedicMedhubId] = useState<number | null>(null)
  const [simulatePatientSelection, isSimulating] = useSimulateBetterDocPatientSelectionMutation()

  const submitPatientSelection = () => {
    if (selectedMedicMedhubId === null) {
      return
    }

    simulatePatientSelection({
      variables: { input: { sessionId, medicMedhubId: selectedMedicMedhubId } },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_simulateBetterDocPatientSelection
        onCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
        onClose()
      },
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose()
    }
  }

  const renderMedicChoice = () => {
    switch (recommendationState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-patient-selection-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded": {
        const { recommendation, status, httpStatusCode, errorReason, exchangeId } = recommendationState.data

        if (recommendation === null) {
          return (
            <BetterDocRemoteRequestStatus
              status={status}
              httpStatusCode={httpStatusCode}
              errorReason={errorReason}
              exchangeId={exchangeId}
            />
          )
        }

        const selectableMedics = collectSelectableMedics(recommendation.medics)

        if (selectableMedics.length === 0) {
          return (
            <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-patient-selection-empty">
              {t("admin.betterDoc.patientSelectionDialog.noRecommendedMedic")}
            </p>
          )
        }

        return (
          <RadioGroup
            value={selectedMedicMedhubId === null ? "" : String(selectedMedicMedhubId)}
            onValueChange={(selectedValue) => setSelectedMedicMedhubId(Number(selectedValue))}
            aria-label={t("admin.betterDoc.patientSelectionDialog.medic")}
            data-testid="better-doc-admin-patient-selection-medics">
            {selectableMedics.map((selectableMedic) => (
              <RadioGroupItem
                key={selectableMedic.medhubId}
                id={`better-doc-patient-selection-medic-${selectableMedic.medhubId}`}
                value={String(selectableMedic.medhubId)}
                data-testid={`better-doc-admin-patient-selection-medic-${selectableMedic.medhubId}`}>
                <RadioGroupLabel htmlFor={`better-doc-patient-selection-medic-${selectableMedic.medhubId}`}>
                  {selectableMedic.label}
                </RadioGroupLabel>
              </RadioGroupItem>
            ))}
          </RadioGroup>
        )
      }
    }
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContentCustom variant="small" data-testid="better-doc-admin-patient-selection-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.betterDoc.patientSelectionDialog.title", { sessionId })}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-6 pb-2">
          <p className="text-on-surface-subdued text-sm">{t("admin.betterDoc.patientSelectionDialog.hint")}</p>
          {renderMedicChoice()}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="tertiary"
            onClick={onClose}
            data-testid="better-doc-admin-patient-selection-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <Button
            type="button"
            isLoading={isSimulating}
            disabled={selectedMedicMedhubId === null || isSimulating}
            onClick={submitPatientSelection}
            data-testid="better-doc-admin-patient-selection-submit-button">
            {t("common.button.send")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}
